import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Bookmark as BookmarkIcon,
  Computer as ComputerIcon,
  Login as LoginIcon,
  Person as PersonIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTip,
  XAxis,
  YAxis,
} from "recharts";
import PageContainer from "../../ui/layout/PageContainer";
import { getRequestWithToken } from "../../utils/ApiRequests";
import "./analytics.scss";

// ─── constants ───────────────────────────────────────────────────────────────
const PALETTE = [
  "#2563EB", "#16A34A", "#7C3AED", "#EA580C",
  "#0891B2", "#DB2777", "#D97706", "#65A30D",
  "#DC2626", "#9333EA", "#0D9488", "#B45309",
];

const EVENT_TYPES = [
  { value: "login",           label: "Login" },
  { value: "logout",          label: "Logout" },
  { value: "apply_job",       label: "Apply Job" },
  { value: "save_job",        label: "Save Job" },
  { value: "job_view",        label: "Job View" },
  { value: "search",          label: "Search" },
  { value: "filter_apply",    label: "Filter Apply" },
  { value: "profile_update",  label: "Profile Update" },
  { value: "onboarding_step", label: "Onboarding Step" },
];

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();

const fmtEvent = (v) => {
  const found = EVENT_TYPES.find((e) => e.value === v);
  return found ? found.label : v || "—";
};

const ONBOARDING_STEPS = [
  { value: "personal", label: "Personal Info" },
  { value: "skills",   label: "Skills" },
  { value: "work",     label: "Work Experience" },
];

const fmtStep = (v) => {
  const found = ONBOARDING_STEPS.find((s) => s.value === v);
  return found ? found.label : v || "—";
};

const toLocalDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const todayStr = () => new Date().toISOString().slice(0, 10);

const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Pivot rows of { date, [keyField], [valueField] } into
 * [ { date, key1: val, key2: val, ... } ] sorted by date.
 */
const pivotByDate = (rows, keyField, valueField) => {
  const map = {};
  rows.forEach((r) => {
    if (!map[r.date]) map[r.date] = { date: r.date };
    map[r.date][r[keyField]] = (map[r.date][r[keyField]] || 0) + Number(r[valueField] || 0);
  });
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
};

/** Aggregate rows by a single key, summing counts. Returns [{ key, count }] desc. */
const aggregateByKey = (rows, keyField, valueField = "count") => {
  const map = {};
  rows.forEach((r) => {
    const k = r[keyField] || "unknown";
    map[k] = (map[k] || 0) + Number(r[valueField] || 0);
  });
  return Object.entries(map)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
};

const getJobDisplay = (row) => {
  const primary = row.jobCode || row.jobTitle || row.jobId || "—";
  const secondary = row.jobTitle && row.jobTitle !== row.jobCode ? row.jobTitle : "";
  return { primary, secondary, mongoId: row.jobId || "" };
};

// ─── sub-components ──────────────────────────────────────────────────────────
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box mt={2}>{children}</Box>}
    </div>
  );
}

function KpiCard({ title, value, icon, color, loading, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.2s, transform 0.15s",
        "&:hover": onClick ? { boxShadow: 6, transform: "translateY(-2px)" } : {},
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: 2,
              bgcolor: `${color}22`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
          </Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
        </Stack>
        {loading
          ? <Skeleton width={80} height={36} />
          : <Typography variant="h5" fontWeight={700} mt={0.5}>{fmt(value)}</Typography>
        }
      </CardContent>
    </Card>
  );
}

function DateFilter({
  startDate,
  endDate,
  onStart,
  onEnd,
  onApply,
  onClearError,
  loading,
  error,
}) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          label="From" type="date" size="small" value={startDate}
          onChange={(e) => {
            if (error) onClearError();
            onStart(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayStr() }}
          error={Boolean(error)}
          sx={{ minWidth: 155 }}
        />
        <TextField
          label="To" type="date" size="small" value={endDate}
          onChange={(e) => {
            if (error) onClearError();
            onEnd(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: todayStr() }}
          error={Boolean(error)}
          sx={{ minWidth: 155 }}
        />
        <Button
          variant="contained" size="small" disabled={loading}
          onClick={onApply} startIcon={<RefreshIcon />} sx={{ height: 40 }}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={loading}
          onClick={() => {
            onClearError();
            onStart(daysAgoStr(30));
            onEnd(todayStr());
          }}
          sx={{ height: 40 }}
        >
          Reset
        </Button>
      </Stack>
      {error ? (
        <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

function ChartCard({ title, children, loading, height = 260, action }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          {action}
        </Stack>
        {loading
          ? <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: 1 }} />
          : children
        }
      </CardContent>
    </Card>
  );
}

function NoData() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" height={200}>
      <Typography color="text.secondary" fontSize={13}>No data for selected range</Typography>
    </Box>
  );
}

const USER_TABLE_HEADERS = [
  { label: "Type", sx: { minWidth: 90, whiteSpace: "nowrap" } },
  { label: "User ID", sx: { minWidth: 120, whiteSpace: "nowrap" } },
  { label: "Mobile", sx: { minWidth: 105, whiteSpace: "nowrap" } },
  { label: "Email", sx: { minWidth: 190, whiteSpace: "nowrap" } },
  { label: "Last Login", sx: { minWidth: 130, whiteSpace: "nowrap" } },
  { label: "Platform", sx: { minWidth: 90, whiteSpace: "nowrap" } },
  { label: "Total Logins", sx: { minWidth: 90, whiteSpace: "nowrap" } },
];

const LAST_ACTIVITY_HEADERS = [
  { label: "Type", sx: { minWidth: 90, whiteSpace: "nowrap" } },
  { label: "User ID", sx: { minWidth: 120, whiteSpace: "nowrap" } },
  { label: "Mobile", sx: { minWidth: 105, whiteSpace: "nowrap" } },
  { label: "Email", sx: { minWidth: 190, whiteSpace: "nowrap" } },
  { label: "Last Activity", sx: { minWidth: 130, whiteSpace: "nowrap" } },
  { label: "Platform", sx: { minWidth: 90, whiteSpace: "nowrap" } },
  { label: "Last Event", sx: { minWidth: 120, whiteSpace: "nowrap" } },
  { label: "Job", sx: { minWidth: 180, whiteSpace: "nowrap" } },
  { label: "Total Events", sx: { minWidth: 90, whiteSpace: "nowrap" } },
  { label: "Journey", sx: { minWidth: 80, whiteSpace: "nowrap" } },
];

// ─── main component ───────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── tab state ──────────────────────────────────────────────────────────────
  const [tab, setTabState] = useState(() => {
    const t = parseInt(searchParams.get("tab"));
    return isNaN(t) ? 0 : t;
  });
  const setTab = (v) => {
    setTabState(v);
    setSearchParams((p) => { p.set("tab", v); return p; }, { replace: true });
  };
  const [activityTab, setActivityTab] = useState(0);
  const [userTab, setUserTab] = useState(0);

  // ── shared date filter ─────────────────────────────────────────────────────
  const [startDate, setStartDate] = useState(daysAgoStr(15));
  const [endDate, setEndDate] = useState(todayStr());
  const [applied, setApplied] = useState({ start: daysAgoStr(15), end: todayStr() });
  const [dateError, setDateError] = useState("");

  // ── overview ───────────────────────────────────────────────────────────────
  const [overview, setOverview] = useState(null);
  const [platformData, setPlatformData] = useState([]);
  const [dauData, setDauData] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);

  // ── events tab ─────────────────────────────────────────────────────────────
  const [eventFilter, setEventFilter] = useState("all");
  const [eventCountsData, setEventCountsData] = useState([]);
  const [loginTrendsData, setLoginTrendsData] = useState([]);
  const [appsByPlatform, setAppsByPlatform] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // ── activity tab ───────────────────────────────────────────────────────────
  const [candActivity, setCandActivity] = useState([]);
  const [empActivity, setEmpActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // ── users tab ──────────────────────────────────────────────────────────────
  const [lastLoginData, setLastLoginData] = useState([]);
  const [lastLoginPag, setLastLoginPag] = useState({ page: 1, pages: 1, total: 0 });
  const [lastLoginPage, setLastLoginPage] = useState(1);
  const [lastLoginFilters, setLastLoginFilters] = useState({ userType: "candidate", source: "" });

  const [lastActivityData, setLastActivityData] = useState([]);
  const [lastActivityPag, setLastActivityPag] = useState({ page: 1, pages: 1, total: 0 });
  const [lastActivityPage, setLastActivityPage] = useState(1);
  const [lastActivityFilters, setLastActivityFilters] = useState({ userType: "candidate", source: "", eventType: "" });

  const [usersLoading, setUsersLoading] = useState(false);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [journeyUser, setJourneyUser] = useState(null);
  const [journeyData, setJourneyData] = useState([]);
  const [journeyPag, setJourneyPag] = useState({ page: 1, pages: 1, total: 0 });
  const [journeyPage, setJourneyPage] = useState(1);
  const [journeyLoading, setJourneyLoading] = useState(false);

  // ── job events tab ─────────────────────────────────────────────────────────
  const [jobEvents, setJobEvents] = useState([]);
  const [jobEventsPag, setJobEventsPag] = useState({ page: 1, pages: 1, total: 0 });
  const [jobEventsPage, setJobEventsPage] = useState(1);
  const [jobEventsFilter, setJobEventsFilter] = useState("");
  const [jobEventsLoading, setJobEventsLoading] = useState(false);

  // ── onboarding steps ──────────────────────────────────────────────────────
  const [onboardingData, setOnboardingData] = useState([]);
  const [onboardingPag, setOnboardingPag] = useState({ page: 1, pages: 1, total: 0 });
  const [onboardingPage, setOnboardingPage] = useState(1);
  const [onboardingStep, setOnboardingStep] = useState("");
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // ── top viewed jobs ────────────────────────────────────────────────────────
  const [topJobs, setTopJobs] = useState([]);
  const [topJobsLoading, setTopJobsLoading] = useState(false);
  const [topAppliedJobsLoading, setTopAppliedJobsLoading] = useState(false);
  const [overviewStaticsLoading, setOverviewStaticsLoading] = useState(false);
  const [topJobsPag, setTopJobsPag] = useState({ page: 1, pages: 1, total: 0 });
  const [topJobsPage, setTopJobsPage] = useState(1);
  const [topAppliedJobs, setTopAppliedJobs] = useState([]);
  const [topAppliedJobsPag, setTopAppliedJobsPag] = useState({ page: 1, pages: 1, total: 0 });
  const [topAppliedJobsPage, setTopAppliedJobsPage] = useState(1);
  const [conversionFunnel, setConversionFunnel] = useState({ steps: [], conversions: {} });
  const [onboardingDropoff, setOnboardingDropoff] = useState({ steps: [], dropoff: {} });
  const [authSplit, setAuthSplit] = useState([]);

  // ── inactive users ──────────────────────────────────────────────────────────
  const [inactiveData, setInactiveData] = useState([]);
  const [inactivePag, setInactivePag] = useState({ page: 1, pages: 1, total: 0 });
  const [inactivePage, setInactivePage] = useState(1);
  const [inactiveFilter, setInactiveFilter] = useState({ userType: "candidate", inactiveSince: "30" });
  const [inactiveLoading, setInactiveLoading] = useState(false);

  const [error, setError] = useState(null);

  const dq = `startDate=${applied.start}&endDate=${applied.end}`;

  // ── fetchers ───────────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const [ovRes, platRes, dauRes] = await Promise.all([
        getRequestWithToken(`/analytics-admin/overview?${dq}`),
        getRequestWithToken(`/analytics-admin/platform-summary?${dq}`),
        getRequestWithToken(`/analytics-admin/daily-active-users?${dq}`),
      ]);
      setOverview(ovRes.data?.data || null);
      setPlatformData(
        (platRes.data?.data || []).map((r) => ({
          name: r.platform === "web" ? "Web" : "App",
          value: Number(r.uniqueUsers || 0),
        }))
      );
      setDauData(pivotByDate(dauRes.data?.data || [], "platform", "activeUsers"));
    } catch {
      setError("Failed to load overview data.");
    } finally {
      setOverviewLoading(false);
    }
  }, [dq]);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const evParams = eventFilter !== "all" ? `${dq}&eventType=${eventFilter}` : dq;
      const [evRes, lgRes, appRes] = await Promise.all([
        getRequestWithToken(`/analytics-admin/event-counts-by-date?${evParams}`),
        getRequestWithToken(`/analytics-admin/login-trends?${dq}`),
        getRequestWithToken(`/analytics-admin/applications-by-platform?${dq}`),
      ]);
      setEventCountsData(pivotByDate(evRes.data?.data || [], "eventType", "count"));
      setLoginTrendsData(pivotByDate(lgRes.data?.data || [], "userType", "count"));
      setAppsByPlatform(pivotByDate(appRes.data?.data || [], "platform", "count"));
    } catch {
      setError("Failed to load event data.");
    } finally {
      setEventsLoading(false);
    }
  }, [dq, eventFilter]);

  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const [candRes] = await Promise.all([
        getRequestWithToken(`/analytics-admin/candidate-activity?${dq}`),
      ]);
      setCandActivity(aggregateByKey(candRes.data?.data || [], "eventType"));
    } catch {
      setError("Failed to load activity data.");
    } finally {
      setActivityLoading(false);
    }
  }, [dq]);

  const fetchLastLogin = useCallback(async () => {
    setUsersLoading(true);
    try {
      const q = new URLSearchParams({ page: lastLoginPage, limit: 20 });
      if (lastLoginFilters.userType) q.set("userType", lastLoginFilters.userType);
      if (lastLoginFilters.source) q.set("source", lastLoginFilters.source);
      const res = await getRequestWithToken(`/analytics-admin/last-login?${q}`);
      setLastLoginData(res.data?.data || []);
      setLastLoginPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load login data.");
    } finally {
      setUsersLoading(false);
    }
  }, [lastLoginPage, lastLoginFilters]);

  const fetchLastActivity = useCallback(async () => {
    setUsersLoading(true);
    try {
      const q = new URLSearchParams({ page: lastActivityPage, limit: 20 });
      if (lastActivityFilters.userType) q.set("userType", lastActivityFilters.userType);
      if (lastActivityFilters.source) q.set("source", lastActivityFilters.source);
      if (lastActivityFilters.eventType) q.set("eventType", lastActivityFilters.eventType);
      const res = await getRequestWithToken(`/analytics-admin/last-activity?${q}`);
      setLastActivityData(res.data?.data || []);
      setLastActivityPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load activity data.");
    } finally {
      setUsersLoading(false);
    }
  }, [lastActivityPage, lastActivityFilters]);

  const fetchUserJourney = useCallback(async (userId, page = 1) => {
    if (!userId) return;
    setJourneyLoading(true);
    try {
      const q = new URLSearchParams({
        page: String(page),
        limit: "15",
        startDate: applied.start,
        endDate: applied.end,
      });
      const res = await getRequestWithToken(`/analytics-admin/user-journey/${userId}?${q}`);
      setJourneyData(res.data?.data || []);
      setJourneyPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load user journey.");
    } finally {
      setJourneyLoading(false);
    }
  }, [applied]);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);
  useEffect(() => { fetchActivity(); }, [fetchActivity]);
  useEffect(() => { if (tab === 3 && userTab === 0) fetchLastLogin(); }, [fetchLastLogin, tab, userTab]);
  useEffect(() => { if (tab === 3 && userTab === 1) fetchLastActivity(); }, [fetchLastActivity, tab, userTab]);
  useEffect(() => {
    if (journeyOpen && journeyUser?.userId) {
      fetchUserJourney(journeyUser.userId, journeyPage);
    }
  }, [journeyOpen, journeyPage, journeyUser, fetchUserJourney]);

  const fetchJobEvents = useCallback(async () => {
    setJobEventsLoading(true);
    try {
      const q = new URLSearchParams({ page: jobEventsPage, limit: 20, startDate: applied.start, endDate: applied.end });
      if (jobEventsFilter) q.set("eventType", jobEventsFilter);
      const res = await getRequestWithToken(`/analytics-admin/job-events?${q}`);
      setJobEvents(res.data?.data || []);
      setJobEventsPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load job events.");
    } finally {
      setJobEventsLoading(false);
    }
  }, [jobEventsPage, jobEventsFilter, applied]);

  useEffect(() => { if (tab === 1) fetchJobEvents(); }, [fetchJobEvents, tab]);

  const fetchOnboardingSteps = useCallback(async () => {
    setOnboardingLoading(true);
    try {
      const q = new URLSearchParams({ page: onboardingPage, limit: 10, startDate: applied.start, endDate: applied.end });
      if (onboardingStep) q.set("step", onboardingStep);
      const res = await getRequestWithToken(`/analytics-admin/onboarding-steps?${q}`);
      setOnboardingData(res.data?.data || []);
      setOnboardingPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load onboarding steps.");
    } finally {
      setOnboardingLoading(false);
    }
  }, [onboardingPage, onboardingStep, applied]);

  useEffect(() => { if (tab === 1) fetchOnboardingSteps(); }, [fetchOnboardingSteps, tab]);

  const fetchTopViewedJobs = useCallback(async () => {
    setTopJobsLoading(true);
    try {
      const res = await getRequestWithToken(`/analytics-admin/top-viewed-jobs?${dq}&page=${topJobsPage}&limit=10`);
      setTopJobs(res.data?.data || []);
      setTopJobsPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load top viewed jobs.");
    } finally {
      setTopJobsLoading(false);
    }
  }, [dq, topJobsPage]);

  const fetchTopAppliedJobs = useCallback(async () => {
    setTopAppliedJobsLoading(true);
    try {
      const res = await getRequestWithToken(`/analytics-admin/top-applied-jobs?${dq}&page=${topAppliedJobsPage}&limit=10`);
      setTopAppliedJobs(res.data?.data || []);
      setTopAppliedJobsPag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load top applied jobs.");
    } finally {
      setTopAppliedJobsLoading(false);
    }
  }, [dq, topAppliedJobsPage]);

  const fetchOverviewStatics = useCallback(async () => {
    setOverviewStaticsLoading(true);
    try {
      const [funnelRes, dropoffRes, authSplitRes] = await Promise.all([
        getRequestWithToken(`/analytics-admin/conversion-funnel?${dq}`),
        getRequestWithToken(`/analytics-admin/onboarding-dropoff?${dq}`),
        getRequestWithToken(`/analytics-admin/authenticated-split?${dq}`),
      ]);
      setConversionFunnel(funnelRes.data?.data || { steps: [], conversions: {} });
      setOnboardingDropoff(dropoffRes.data?.data || { steps: [], dropoff: {} });
      setAuthSplit(authSplitRes.data?.data || []);
    } catch {
      setError("Failed to load overview analytics.");
    } finally {
      setOverviewStaticsLoading(false);
    }
  }, [dq]);

  const fetchInactiveUsers = useCallback(async () => {
    setInactiveLoading(true);
    try {
      const q = new URLSearchParams({ page: inactivePage, limit: 20 });
      if (inactiveFilter.userType) q.set("userType", inactiveFilter.userType);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - parseInt(inactiveFilter.inactiveSince || 30));
      q.set("inactiveSince", cutoff.toISOString().slice(0, 10));
      const res = await getRequestWithToken(`/analytics-admin/inactive-users?${q}`);
      setInactiveData(res.data?.data || []);
      setInactivePag(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      setError("Failed to load inactive users.");
    } finally {
      setInactiveLoading(false);
    }
  }, [inactivePage, inactiveFilter]);

  useEffect(() => { if (tab === 0) fetchTopViewedJobs(); }, [fetchTopViewedJobs, tab]);
  useEffect(() => { if (tab === 0) fetchTopAppliedJobs(); }, [fetchTopAppliedJobs, tab]);
  useEffect(() => { if (tab === 0) fetchOverviewStatics(); }, [fetchOverviewStatics, tab]);
  useEffect(() => { if (tab === 3 && userTab === 2) fetchInactiveUsers(); }, [fetchInactiveUsers, tab, userTab]);

  // ── derived keys for dynamic chart lines ───────────────────────────────────
  const KNOWN_EVENT_VALUES = new Set(EVENT_TYPES.map((e) => e.value));
  const eventKeys = [...new Set(
    eventCountsData.flatMap((r) => Object.keys(r).filter((k) => k !== "date" && KNOWN_EVENT_VALUES.has(k)))
  )];
  const loginKeys = [...new Set(
    loginTrendsData.flatMap((r) => Object.keys(r).filter((k) => k !== "date" && k !== "employer"))
  )];
  const authSplitChartData = ["job_view", "save_job", "apply_job"].map((eventType) => {
    const rows = authSplit.filter((row) => row.eventType === eventType);
    const row = { eventType };
    rows.forEach((item) => {
      row[item.cohort] = Number(item.count || 0);
    });
    row.authenticated = row.authenticated || 0;
    row.anonymous = row.anonymous || 0;
    return row;
  });

  const openJourney = (row) => {
    setJourneyUser(row);
    setJourneyPage(1);
    setJourneyOpen(true);
  };

  const closeJourney = () => {
    setJourneyOpen(false);
    setJourneyUser(null);
    setJourneyData([]);
    setJourneyPag({ page: 1, pages: 1, total: 0 });
    setJourneyPage(1);
  };

  const applyDates = () => {
    if (!startDate || !endDate) {
      setDateError("Select both From and To dates.");
      return;
    }

    if (startDate > endDate) {
      setDateError("From date cannot be later than To date.");
      return;
    }

    if (startDate > todayStr() || endDate > todayStr()) {
      setDateError("Future dates are not allowed.");
      return;
    }

    setDateError("");
    setApplied({ start: startDate, end: endDate });
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <PageContainer>
      {/* Header */}
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        mb={2} flexWrap="wrap" gap={2}
      >
        <Typography variant="h5" fontWeight={700}>Analytics Dashboard</Typography>
        <DateFilter
          startDate={startDate} endDate={endDate}
          onStart={setStartDate} onEnd={setEndDate}
          onApply={applyDates}
          onClearError={() => setDateError("")}
          loading={overviewLoading || eventsLoading}
          error={dateError}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Overview" />
          <Tab label="Events" />
          <Tab label="User Activity" />
          <Tab label="Users" />
        </Tabs>
      </Box>

      {/* ══ TAB 0: Overview ══════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={0}>
        {/* KPI cards */}
        <Grid container spacing={2} mb={3}>
          {[
            {
              title: "Total Events", key: "totalEvents", icon: <TrendingUpIcon />, color: "#2563EB",
              action: () => { setTab(1); setEventFilter("all"); },
            },
            {
              title: "Web Users", key: "webUsers", icon: <ComputerIcon />, color: "#0891B2",
              action: () => { setTab(3); setUserTab(0); setLastLoginFilters({ userType: "", source: "web" }); setLastLoginPage(1); },
            },
            {
              title: "App Users", key: "appUsers", icon: <PhoneAndroidIcon />, color: "#16A34A",
              action: () => { setTab(3); setUserTab(0); setLastLoginFilters({ userType: "", source: "app" }); setLastLoginPage(1); },
            },
            {
              title: "Applications", key: "totalApplications", icon: <WorkIcon />, color: "#7C3AED",
              action: () => { setTab(1); setEventFilter("apply_job"); },
            },
            {
              title: "Logins", key: "totalLogins", icon: <LoginIcon />, color: "#EA580C",
              action: () => { setTab(3); setUserTab(0); setLastLoginFilters({ userType: "", source: "" }); setLastLoginPage(1); },
            },
            {
              title: "Candidate Events", key: "candidateEvents", icon: <PersonIcon />, color: "#DB2777",
              action: () => { setTab(2); setActivityTab(0); },
            },
            {
              title: "Job Views", key: "totalJobViews", icon: <VisibilityIcon />, color: "#0D9488",
              action: () => { setTab(1); setEventFilter("job_view"); },
            },
            {
              title: "Saves", key: "totalSaveJobs", icon: <BookmarkIcon />, color: "#9333EA",
              action: () => { setTab(1); setEventFilter("save_job"); },
            },
          ].map(({ title, key, icon, color, action }) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
              <KpiCard
                title={title} value={overview?.[key]}
                icon={icon} color={color} loading={overviewLoading}
                onClick={action}
              />
            </Grid>
          ))}
        </Grid>

        {/* Platform pie + DAU line */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <ChartCard title="Platform Split" loading={overviewLoading} height={250}>
              {platformData.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={platformData} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" outerRadius={90}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {platformData.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <RechartsTip formatter={fmt} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={8}>
            <ChartCard title="Daily Active Users" loading={overviewLoading} height={250}>
              {dauData.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dauData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTip formatter={fmt} />
                    <Legend />
                    <Line type="monotone" dataKey="web" stroke="#2563EB" dot={false} strokeWidth={2} name="Web" />
                    <Line type="monotone" dataKey="app" stroke="#16A34A" dot={false} strokeWidth={2} name="App" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Conversion Funnel" loading={overviewStaticsLoading} height={280}>
              {conversionFunnel.steps?.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={conversionFunnel.steps} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmt} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={100} />
                    <RechartsTip formatter={fmt} />
                    <Bar dataKey="count" fill="#2563EB" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {conversionFunnel.steps?.length > 0 && (
                <Stack direction="row" spacing={2} flexWrap="wrap" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    View→Save: {conversionFunnel.conversions?.viewToSavePct || 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    View→Apply: {conversionFunnel.conversions?.viewToApplyPct || 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Save→Apply: {conversionFunnel.conversions?.saveToApplyPct || 0}%
                  </Typography>
                </Stack>
              )}
            </ChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <ChartCard title="Authenticated vs Anonymous" loading={overviewStaticsLoading} height={280}>
              {authSplitChartData.every((row) => !row.authenticated && !row.anonymous) ? <NoData /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={authSplitChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="eventType" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTip formatter={fmt} />
                    <Legend />
                    <Bar dataKey="authenticated" stackId="a" fill="#16A34A" name="Authenticated" />
                    <Bar dataKey="anonymous" stackId="a" fill="#EA580C" name="Anonymous" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Onboarding Dropoff" loading={overviewStaticsLoading} height={280}>
              {onboardingDropoff.steps?.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={onboardingDropoff.steps}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTip formatter={fmt} />
                    <Bar dataKey="users" fill="#7C3AED" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              {onboardingDropoff.steps?.length > 0 && (
                <Stack direction="row" spacing={2} flexWrap="wrap" mt={1}>
                  <Typography variant="caption" color="text.secondary">
                    Personal→Skills dropoff: {onboardingDropoff.dropoff?.personalToSkillsPct || 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skills→Work dropoff: {onboardingDropoff.dropoff?.skillsToWorkPct || 0}%
                  </Typography>
                </Stack>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* Top Viewed Jobs */}
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Top Viewed Jobs</Typography>
          {topJobsLoading ? (
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          ) : topJobs.length === 0 ? (
            <NoData />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    {["#", "Job Code", "Job Title", "Views"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topJobs.map((row, i) => (
                    <TableRow key={row.jobId || i} hover>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace", fontWeight: 600 }}>
                        {row.jobCode || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{row.jobTitle || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#2563EB" }}>
                        {fmt(row.views)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {topJobsPag.pages > 1 && (
            <Stack alignItems="center" mt={2}>
              <Pagination
                count={topJobsPag.pages}
                page={topJobsPage}
                onChange={(_, p) => setTopJobsPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            {fmt(topJobsPag.total)} total records
          </Typography>
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>Top Applied Jobs</Typography>
          {topAppliedJobsLoading ? (
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
          ) : topAppliedJobs.length === 0 ? (
            <NoData />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    {["#", "Job Code", "Job Title", "Applications"].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAppliedJobs.map((row, i) => (
                    <TableRow key={row.jobId || i} hover>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace", fontWeight: 600 }}>
                        {row.jobCode || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{row.jobTitle || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 700, color: "#16A34A" }}>
                        {fmt(row.applies)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {topAppliedJobsPag.pages > 1 && (
            <Stack alignItems="center" mt={2}>
              <Pagination
                count={topAppliedJobsPag.pages}
                page={topAppliedJobsPage}
                onChange={(_, p) => setTopAppliedJobsPage(p)}
                color="primary"
                size="small"
              />
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            {fmt(topAppliedJobsPag.total)} total records
          </Typography>
        </Box>
      </TabPanel>

      {/* ══ TAB 1: Events ════════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={1}>
        {/* Event counts by date */}
        <ChartCard
          title="Event Counts by Date"
          loading={eventsLoading}
          height={280}
          action={
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Event Type</InputLabel>
              <Select
                label="Event Type" value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
              >
                <MenuItem value="all">All Events</MenuItem>
                {EVENT_TYPES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        >
          {eventCountsData.length === 0 ? <NoData /> : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={eventCountsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTip formatter={fmt} />
                <Legend />
                {eventKeys.map((key, i) => (
                  <Line
                    key={key} type="monotone" dataKey={key} name={fmtEvent(key)}
                    stroke={PALETTE[i % PALETTE.length]} dot={false} strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <Grid container spacing={2} mt={0.5}>
          {/* Login trends */}
          <Grid item xs={12} md={6}>
            <ChartCard title="Login Trends" loading={eventsLoading} height={250}>
              {loginTrendsData.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={loginTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTip formatter={fmt} />
                    <Legend />
                    {loginKeys.map((key, i) => (
                      <Line
                        key={key} type="monotone" dataKey={key}
                        stroke={PALETTE[i % PALETTE.length]} dot={false} strokeWidth={2}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>

          {/* Applications by platform */}
          <Grid item xs={12} md={6}>
            <ChartCard title="Applications by Platform" loading={eventsLoading} height={250}>
              {appsByPlatform.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={appsByPlatform}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTip formatter={fmt} />
                    <Legend />
                    <Bar dataKey="web" stackId="a" fill="#2563EB" name="Web" />
                    <Bar dataKey="app" stackId="a" fill="#16A34A" name="App" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </Grid>
        </Grid>

        {/* Job Events table */}
        <Box mt={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Job Events (Apply Job / Save Job / Job View)
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Event Type</InputLabel>
              <Select
                label="Event Type" value={jobEventsFilter}
                onChange={(e) => { setJobEventsFilter(e.target.value); setJobEventsPage(1); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="apply_job">Apply Job</MenuItem>
                <MenuItem value="save_job">Save Job</MenuItem>
                <MenuItem value="job_view">Job View</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {jobEventsLoading ? (
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      {["Event", "Job Details", "Candidate", "Contact", "Platform", "Date"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" fontSize={13} py={2}>No data</Typography>
                        </TableCell>
                      </TableRow>
                    ) : jobEvents.map((row) => {
                      const jobDisplay = getJobDisplay(row);
                      const userIdentity = row.candidateId || row.userId || "—";
                      const contactValue = row.email || row.mobile || "—";

                      return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={fmtEvent(row.eventType)} size="small" variant="outlined"
                            color={row.eventType === "apply_job" ? "primary" : row.eventType === "save_job" ? "secondary" : "default"}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Typography fontSize={12} fontFamily="monospace" fontWeight={600} lineHeight={1.2}>
                            {jobDisplay.primary}
                          </Typography>
                          {jobDisplay.secondary && (
                            <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                              {jobDisplay.secondary}
                            </Typography>
                          )}
                          {jobDisplay.mongoId && (
                            <Typography fontSize={11} color="text.secondary" fontFamily="monospace" sx={{ mt: 0.25 }}>
                              Mongo ID: {jobDisplay.mongoId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Typography fontSize={12} fontFamily="monospace" fontWeight={600}>
                            {userIdentity}
                          </Typography>
                          <Typography fontSize={11} color="text.secondary" sx={{ textTransform: "capitalize" }}>
                            {row.userType || "unknown"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Typography fontSize={12}>
                            {contactValue}
                          </Typography>
                          {!row.email && !row.mobile && (
                            <Typography fontSize={11} color="text.secondary">
                              Missing in tracked activity
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip label={row.source || "—"} size="small" variant="outlined"
                            color={row.source === "web" ? "info" : "success"} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{toLocalDate(row.createdAt)}</TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </TableContainer>
              {jobEventsPag.pages > 1 && (
                <Stack alignItems="center" mt={2}>
                  <Pagination
                    count={jobEventsPag.pages} page={jobEventsPage}
                    onChange={(_, p) => setJobEventsPage(p)} color="primary" size="small"
                  />
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                {fmt(jobEventsPag.total)} total records
              </Typography>
            </>
          )}
        </Box>

        {/* Onboarding Steps table */}
        <Box mt={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Onboarding Steps
            </Typography>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Step</InputLabel>
              <Select
                label="Step" value={onboardingStep}
                onChange={(e) => { setOnboardingStep(e.target.value); setOnboardingPage(1); }}
              >
                <MenuItem value="">All Steps</MenuItem>
                {ONBOARDING_STEPS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {onboardingLoading ? (
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      {["Step", "Context", "Candidate ID", "Email", "Platform", "Date"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {onboardingData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary" fontSize={13} py={2}>No data</Typography>
                        </TableCell>
                      </TableRow>
                    ) : onboardingData.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={fmtStep(row.step)} size="small"
                            color={
                              row.step === "personal" ? "primary"
                              : row.step === "skills" ? "secondary"
                              : "success"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.context === "update" ? "Profile Update" : row.context === "onboarding" ? "Onboarding" : "—"}
                            size="small"
                            variant="outlined"
                            color={row.context === "update" ? "warning" : row.context === "onboarding" ? "info" : "default"}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                          {row.candidateId || "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{row.email || "—"}</TableCell>
                        <TableCell>
                          <Chip label={row.source || "—"} size="small" variant="outlined"
                            color={row.source === "web" ? "info" : "success"} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{toLocalDate(row.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {onboardingPag.pages > 1 && (
                <Stack alignItems="center" mt={2}>
                  <Pagination
                    count={onboardingPag.pages} page={onboardingPage}
                    onChange={(_, p) => setOnboardingPage(p)} color="primary" size="small"
                  />
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                {fmt(onboardingPag.total)} total records
              </Typography>
            </>
          )}
        </Box>
      </TabPanel>

      {/* ══ TAB 2: User Activity ═════════════════════════════════════════════ */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activityTab} onChange={(_, v) => setActivityTab(v)}>
            <Tab label="Candidate Activity" />
          </Tabs>
        </Box>

        {activityTab === 0 && (
          <ChartCard title="Candidate Events by Type" loading={activityLoading} height={320}>
            {candActivity.length === 0 ? <NoData /> : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={candActivity} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmt} />
                  <YAxis type="category" dataKey="key" tick={{ fontSize: 11 }} width={145} />
                  <RechartsTip formatter={fmt} />
                  <Bar dataKey="count" fill="#7C3AED" name="Events" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        )}


      </TabPanel>

      {/* ══ TAB 3: Users ═════════════════════════════════════════════════════ */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={userTab} onChange={(_, v) => setUserTab(v)}>
            <Tab label="Last Login" />
            <Tab label="Last Activity" />
            <Tab label="Inactive Users" />
          </Tabs>
        </Box>

        {/* Last Login */}
        {userTab === 0 && (
          <Box>
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform" value={lastLoginFilters.source}
                  onChange={(e) => {
                    setLastLoginFilters((f) => ({ ...f, source: e.target.value }));
                    setLastLoginPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="app">App</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {usersLoading ? (
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        {USER_TABLE_HEADERS.map((header) => (
                          <TableCell key={header.label} sx={{ fontWeight: 600, fontSize: 13, ...header.sx }}>
                            {header.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lastLoginData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary" fontSize={13} py={2}>No data</Typography>
                          </TableCell>
                        </TableRow>
                      ) : lastLoginData.map((row) => (
                        <TableRow key={row.userId} hover>
                          <TableCell>
                            <Chip
                              label={row.userType || "—"} size="small" variant="outlined"
                              color={row.userType === "candidate" ? "secondary" : "warning"}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.candidateId || "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.mobile || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.email || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{toLocalDate(row.lastLogin)}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.lastLoginSource || "—"} size="small" variant="outlined"
                              color={row.lastLoginSource === "web" ? "primary" : "success"}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{fmt(row.totalLogins)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {lastLoginPag.pages > 1 && (
                  <Stack alignItems="center" mt={2}>
                    <Pagination
                      count={lastLoginPag.pages} page={lastLoginPage}
                      onChange={(_, p) => setLastLoginPage(p)} color="primary" size="small"
                    />
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  {fmt(lastLoginPag.total)} total records
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Last Activity */}
        {userTab === 1 && (
          <Box>
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform" value={lastActivityFilters.source}
                  onChange={(e) => {
                    setLastActivityFilters((f) => ({ ...f, source: e.target.value }));
                    setLastActivityPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="app">App</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  label="Event Type" value={lastActivityFilters.eventType}
                  onChange={(e) => {
                    setLastActivityFilters((f) => ({ ...f, eventType: e.target.value }));
                    setLastActivityPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {EVENT_TYPES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {usersLoading ? (
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        {LAST_ACTIVITY_HEADERS.map((header) => (
                          <TableCell key={header.label} sx={{ fontWeight: 600, fontSize: 13, ...header.sx }}>
                            {header.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lastActivityData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Typography color="text.secondary" fontSize={13} py={2}>No data</Typography>
                          </TableCell>
                        </TableRow>
                      ) : lastActivityData.map((row) => (
                        <TableRow key={row.userId} hover>
                          <TableCell>
                            <Chip
                              label={row.userType || "—"} size="small" variant="outlined"
                              color={row.userType === "candidate" ? "secondary" : "warning"}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.candidateId || "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.mobile || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.email || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{toLocalDate(row.lastActivity)}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.lastActivitySource || "—"} size="small" variant="outlined"
                              color={row.lastActivitySource === "web" ? "primary" : "success"}
                            />
                          </TableCell>
                          <TableCell>
                            <>
                              <Chip label={fmtEvent(row.lastActivityEvent)} size="small" sx={{ fontSize: 11 }} />
                              {row.lastActivityEvent === "onboarding_step" && row.step && (
                                <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5, textTransform: "capitalize" }}>
                                  Step: {row.step}
                                </Typography>
                              )}
                            </>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {row.jobCode || row.jobTitle || row.jobId ? (
                              <>
                                <Typography fontSize={12} fontFamily="monospace" fontWeight={600} lineHeight={1.2}>
                                  {row.jobCode || row.jobId}
                                </Typography>
                                {row.jobTitle && (
                                  <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                                    {row.jobTitle}
                                  </Typography>
                                )}
                                {row.jobId && (
                                  <Typography fontSize={11} color="text.secondary" fontFamily="monospace">
                                    Mongo ID: {row.jobId}
                                  </Typography>
                                )}
                              </>
                            ) : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#2563EB" }}>
                            {fmt(row.totalEvents)}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View user journey">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openJourney(row)}
                                  disabled={!row.userId}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {lastActivityPag.pages > 1 && (
                  <Stack alignItems="center" mt={2}>
                    <Pagination
                      count={lastActivityPag.pages} page={lastActivityPage}
                      onChange={(_, p) => setLastActivityPage(p)} color="primary" size="small"
                    />
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  {fmt(lastActivityPag.total)} total records
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Inactive Users */}
        {userTab === 2 && (
          <Box>
            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Inactive Since</InputLabel>
                <Select
                  label="Inactive Since" value={inactiveFilter.inactiveSince}
                  onChange={(e) => { setInactiveFilter((f) => ({ ...f, inactiveSince: e.target.value })); setInactivePage(1); }}
                >
                  <MenuItem value="7">7 days</MenuItem>
                  <MenuItem value="14">14 days</MenuItem>
                  <MenuItem value="30">30 days</MenuItem>
                  <MenuItem value="60">60 days</MenuItem>
                  <MenuItem value="90">90 days</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {inactiveLoading ? (
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        {["Type", "Candidate ID", "Email", "Mobile", "Last Active", "Total Events"].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inactiveData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" fontSize={13} py={2}>No inactive users found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : inactiveData.map((row) => (
                        <TableRow key={row.userId} hover>
                          <TableCell>
                            <Chip label={row.userType || "—"} size="small" variant="outlined"
                              color={row.userType === "candidate" ? "secondary" : "warning"} />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                            {row.candidateId || "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.email || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{row.mobile || "—"}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{toLocalDate(row.lastActivity)}</TableCell>
                          <TableCell sx={{ fontSize: 13 }}>{fmt(row.totalEvents)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {inactivePag.pages > 1 && (
                  <Stack alignItems="center" mt={2}>
                    <Pagination
                      count={inactivePag.pages} page={inactivePage}
                      onChange={(_, p) => setInactivePage(p)} color="primary" size="small"
                    />
                  </Stack>
                )}
                <Typography variant="caption" color="text.secondary" mt={1} display="block">
                  {fmt(inactivePag.total)} users inactive for {inactiveFilter.inactiveSince}+ days
                </Typography>
              </>
            )}
          </Box>
        )}
      </TabPanel>

      <Dialog
        open={journeyOpen}
        onClose={closeJourney}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>User Journey</Typography>
            <Typography variant="body2" color="text.secondary">
              {journeyUser?.candidateId || journeyUser?.email || journeyUser?.mobile || "Unknown user"}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={2} flexWrap="wrap" mb={2}>
            <Chip label={`Type: ${journeyUser?.userType || "—"}`} size="small" variant="outlined" />
            <Chip label={`Platform: ${journeyUser?.lastActivitySource || "—"}`} size="small" variant="outlined" />
            <Chip label={`Total Events: ${fmt(journeyUser?.totalEvents)}`} size="small" variant="outlined" />
          </Stack>

          {journeyLoading ? (
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
          ) : journeyData.length === 0 ? (
            <NoData />
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.50" }}>
                      {["When", "Event", "Platform", "Job", "Details"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 13 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journeyData.map((row) => {
                      const { primary, secondary, mongoId } = getJobDisplay(row);
                      const details = [];
                      if (row.metadata?.step) details.push(`Step: ${row.metadata.step}`);
                      if (row.metadata?.query) details.push(`Query: ${row.metadata.query}`);
                      if (row.metadata?.filter) details.push(`Filter: ${row.metadata.filter}`);
                      if (row.metadata?.value) details.push(`Value: ${row.metadata.value}`);
                      if (row.metadata?.section) details.push(`Section: ${row.metadata.section}`);
                      if (row.metadata?.msg) details.push(`Message: ${row.metadata.msg}`);

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                            {toLocalDate(row.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Chip label={fmtEvent(row.eventType)} size="small" sx={{ fontSize: 11 }} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.source || "—"}
                              size="small"
                              variant="outlined"
                              color={row.source === "web" ? "primary" : "success"}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, minWidth: 220 }}>
                            {primary !== "—" ? (
                              <>
                                <Typography fontSize={12} fontFamily="monospace" fontWeight={600} lineHeight={1.2}>
                                  {primary}
                                </Typography>
                                {secondary ? (
                                  <Typography fontSize={11} color="text.secondary">
                                    {secondary}
                                  </Typography>
                                ) : null}
                                {mongoId ? (
                                  <Typography fontSize={11} color="text.secondary" fontFamily="monospace">
                                    Mongo ID: {mongoId}
                                  </Typography>
                                ) : null}
                              </>
                            ) : "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {details.length ? (
                              details.map((item) => (
                                <Typography key={item} fontSize={11.5} color="text.secondary">
                                  {item}
                                </Typography>
                              ))
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {journeyPag.pages > 1 && (
                <Stack alignItems="center" mt={2}>
                  <Pagination
                    count={journeyPag.pages}
                    page={journeyPage}
                    onChange={(_, p) => setJourneyPage(p)}
                    color="primary"
                    size="small"
                  />
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary" mt={1} display="block">
                {fmt(journeyPag.total)} events in selected date range
              </Typography>
            </>
          )}
        </DialogContent>
        <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={closeJourney} variant="outlined" size="small">
            Close
          </Button>
        </Box>
      </Dialog>
    </PageContainer>
  );
};

export default AnalyticsDashboard;
